import { captureError } from '@/utils/sentry';
import { AuthTokenStorage } from './AuthTokenStorage';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { LoginResponse } from 'callabo-api/src';
import EventEmitter from 'eventemitter3';
import { dateIsOver } from 'libs/rtzr-commons/DateUtils';
import { getErrorDetail } from 'libs/error';
import { isRequestIgnoredError, RequestIgnoredError } from 'callabo-api/src/core/request';
import { ApiError } from 'callabo-api/src/core/ApiError';
import { captureMessage } from '@sentry/core';

type EventTypes = 'unauthorized' | 'serviceUnavaliable' | 'accessRestricted';
class FailedToRequestError extends Error {
  constructor(message: string) {
    super(message);
    try {
      this.name = 'FailedToRequestError';
    } catch (e) {
      //
    }

    // Set the prototype explicitly.
    // https://www.typescriptlang.org/docs/handbook/2/classes.html#inheriting-built-in-types
    Object.setPrototypeOf(this, FailedToRequestError.prototype);
  }
}

export class AuthTokenManager {
  private _requestQueue: RequestQueue = undefined;
  private _eventEmitter: EventEmitter<EventTypes> = new EventEmitter<EventTypes>();
  public get axiosInstance(): AxiosInstance {
    return this._axiosInstance;
  }

  public get auth(): AuthTokenStorage {
    return this._auth;
  }

  public get event(): EventEmitter<EventTypes> {
    return this._eventEmitter;
  }

  public static async createAsync(
    deviceId: string,
    onRefresh?: (response: LoginResponse) => void
  ): Promise<AuthTokenManager> {
    const axiosInstance = axios.create();
    const auth = await AuthTokenStorage.createAsync();
    return new AuthTokenManager(axiosInstance, auth, deviceId, onRefresh);
  }

  public static async createForShareAsync(
    accessToken: string,
    refreshToken: string,
    tokenExpiredDate: Date,
    deviceId: string
  ): Promise<AuthTokenManager> {
    const axiosInstance = axios.create();
    const auth = await AuthTokenStorage.createForShareAsync(
      accessToken,
      refreshToken,
      tokenExpiredDate
    );
    return new AuthTokenManager(axiosInstance, auth, deviceId);
  }

  private constructor(
    private _axiosInstance: AxiosInstance,
    private _auth: AuthTokenStorage,
    private _deviceId: string,
    onRefresh?: (response: LoginResponse) => void
  ) {
    this._requestQueue = new RequestQueue(_axiosInstance, _auth);
    buildInterceptorsInVitoApiClient(
      _axiosInstance,
      _auth,
      this._requestQueue,
      this._eventEmitter,
      this._deviceId,
      onRefresh
    );
  }
}

const buildInterceptorsInVitoApiClient = (
  axiosInstance: AxiosInstance,
  auth: AuthTokenStorage,
  requestSubscriber: RequestQueue,
  eventEmitter: EventEmitter<EventTypes>,
  deviceId: string,
  onRefresh: (response: LoginResponse) => void
): void => {
  const refreshIfTokenExpiredAsync = async (config?: AxiosRequestConfig, force?: boolean) => {
    const accessToken = await auth.getAccessTokenAsync();
    const tokenExpiredDate = await auth.getAccessTokenExpiredDate();
    if (!accessToken || (tokenExpiredDate && dateIsOver(new Date(tokenExpiredDate))) || force) {
      const refreshToken = await auth.getRefreshTokenAsync();
      if (!refreshToken) {
        throw new RequestIgnoredError('Access token expired. Refresh token is not found.');
      }

      try {
        const loginResponse = await auth.refreshAsync();
        if (onRefresh && loginResponse) {
          onRefresh(loginResponse);
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 400) {
          const currentRefreshToken = await auth.getRefreshTokenAsync();
          const isRefreshTokenEquals = refreshToken === currentRefreshToken;

          if (!isRefreshTokenEquals) {
            // 다르다는 것은, 누군가 이미 갱신했다는 것.
            return;
          }

          const extra = {
            configUrl: config?.url,
            configMethod: config?.method,
          };
          captureMessage('[400] Failed to token refresh', {
            extra: {
              ...extra,
              base64Extra: Buffer.from(JSON.stringify(extra)).toString('base64'),
            },
          });
        } else {
          captureError(new Error('Failed to token refresh', { cause: e }), {
            extra: {
              errorDetail: getErrorDetail(e),
            },
          });
        }
        throw new RequestIgnoredError('Access token expired. Refresh token is not found.');
      }
    }
  };
  const successRequest = async function (config: AxiosRequestConfig) {
    if (auth.isAuthDenied) {
      return Promise.reject(new RequestIgnoredError('Auth denied'));
    }

    try {
      await refreshIfTokenExpiredAsync(config);
    } catch (e) {
      auth.denyAuth();
      eventEmitter.emit('unauthorized');
      return Promise.reject(e);
    }

    const accessToken = await auth.getAccessTokenAsync();
    config.headers.Authorization = `Bearer ${accessToken}`;
    config.headers['X-Header-DeviceId'] = deviceId;
    return config;
  };
  const errorRequest: (error: any) => any = function (error) {
    return Promise.reject(error);
  };
  axiosInstance.interceptors.request.use(successRequest, errorRequest);

  const successResponse = function (response) {
    return response;
  };
  const errorResponse: (error: any) => any = refreshIfNotAuthorizedAsync;
  axiosInstance.interceptors.response.use(successResponse, errorResponse);

  async function refreshIfNotAuthorizedAsync(error: any) {
    const config = error.config;
    if (error.response && error.response.status === 401) {
      if (auth.isRefreshing) {
        return new Promise((resolve, reject) => {
          requestSubscriber.add({
            resolve,
            reject,
            error,
            config,
          });
        });
      }

      try {
        await refreshIfTokenExpiredAsync(config, true);
      } catch (e) {
        auth.denyAuth();
        requestSubscriber.clear();
        eventEmitter.emit('unauthorized');
        return Promise.reject(e);
      }

      try {
        // console.log(`requestSubscriber retry`);
        await requestSubscriber.runRequestAsync();
        const { config }: { config: AxiosRequestConfig } = error;
        return retryOriginRequestAsync(axiosInstance, auth, config);
      } catch (e) {
        // console.error(e);
        requestSubscriber.runThrowError();
        return Promise.reject(error);
      }
    }

    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.error?.code === 'F001'
    ) {
      // F001: IP block error
      eventEmitter.emit('accessRestricted');
    }
    if (error.response && error.response.status === 503) {
      eventEmitter.emit('serviceUnavaliable');
    }

    if (!isErrorIgnored(error)) {
      const url = error?.config?.url ?? error?.request?.url;
      const method = error?.config?.method ?? error?.request?.method;
      const data = error?.config?.data ?? error?.request?.data;
      const responseData = error?.response?.data;
      const responseStatus = error?.response?.status;
      const extra = {
        requestUrl: url,
        requestMethod: method,
        requestData: data,
        responseData: responseData,
        responseStatus: responseStatus,
      };
      console.error('Failed to request', JSON.stringify(extra, null, 2));
      captureError(new FailedToRequestError(`[${responseStatus}] ${url || 'Unknown URL'}`), {
        extra: {
          ...extra,
          base64Extra: Buffer.from(JSON.stringify(extra)).toString('base64'),
          error: getErrorDetail(error?.response?.data, error?.message || error),
        },
      });
    }
    return Promise.reject(error);
  }
};

function isErrorIgnored(error: any): boolean {
  const ignoredErrorNames = ['CanceledError', 'AbortError'];
  if (ignoredErrorNames.includes(error.name)) {
    return true;
  }

  const ignoredStatusCodes = [0, 401, 403, 404, 409, 412, 425, 500, 503];
  if (ignoredStatusCodes.includes(error.response?.status)) {
    return true;
  }

  if (isRequestIgnoredError(error)) {
    return true;
  }

  if (
    error?.status === 400 &&
    error?.response?.data?.error?.code === 'N001' &&
    error?.response?.data?.error?.detail === 'Nothing to update'
  ) {
    return true;
  }

  const errorDetails = ['timeout exceeded', 'Request aborted'];
  const errorDetail = getErrorDetail(error, error.message || '');
  if (errorDetails.includes(errorDetail)) {
    return true;
  }

  return false;
}

interface RequestQueueItem {
  resolve: (value: unknown) => void;
  reject: (error: any) => void;
  error: any;
  config: AxiosRequestConfig;
}

/**
 * 요청을 모아서 처리하는 클래스
 */
class RequestQueue {
  private _requestQueue: RequestQueueItem[] = [];

  constructor(private _axiosInstance: AxiosInstance, private _auth: AuthTokenStorage) {}

  public add = (request: RequestQueueItem): void => {
    this._requestQueue.push(request);
  };

  public runRequestAsync = async (): Promise<void> => {
    while (this._requestQueue.length) {
      const request = this._requestQueue.pop();
      const originRequest = await retryOriginRequestAsync(
        this._axiosInstance,
        this._auth,
        request.config
      );
      request.resolve(originRequest);
    }
  };

  public runThrowError = (): void => {
    while (this._requestQueue.length) {
      const request = this._requestQueue.pop();
      request.reject(request.error);
    }
  };

  public clear = (): void => {
    this._requestQueue.splice(0, this._requestQueue.length);
  };
}

async function retryOriginRequestAsync(
  axiosInstance: AxiosInstance,
  auth: AuthTokenStorage,
  config: AxiosRequestConfig
) {
  // console.log(`retryOriginRequestAsync ${config.url}`);
  const accessToken = await auth.getAccessTokenAsync();
  config.headers.Authorization = `Bearer ${accessToken}`;
  return axiosInstance(config);
}
